import React, { useEffect, useRef, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import { useNavigation } from '@react-navigation/core'
import { useSelector } from 'react-redux'
import moment from 'moment'

import {
	getAvailableSessions,
	bookSession,
	getWaitlistUser,
	addToWaitlist,
	waitlistAccess,
	getVolunteerProfile,
	getPreviousBookedSessionsCount
} from '../../../services/api/snakSammen'

import { CustomText } from '../../text/StyledText'
import { ButtonPrimary } from '../../buttons/StyledButtons'

import Colors from '../../../constants/Colors'
import { txt } from '../../../locales/i18n'
import { formatDate } from "./utils/helperFunctions"
import Layout from "../../../constants/Layout"
import CustomPressable from "../../pressables/CustomPressable"
import NormalIcon from "../../icons/NormalIcon"
import CustomDialog, { ContentContainer } from "../../dialogs/CustomDialog"


export function AvailableSessions({ availableDates, handleRefresh, setErrorMessage }) {
	const [sessionDates, setSessionDates] = useState([])
	const [sessions, setSessions] = useState([])
	const [volunteerProfile, setVolunteerProfile] = useState(null)
	const [volunteerProfileVisible, setVolunteerProfileVisible] = useState(false)
	const [showWaitlistButton, setShowWaitlistButton] = useState(false)
	const [showWaitlistDialog, setShowWaitlistDialog] = useState(false)
	const [userIsOnWaitlist, setUserIsOnWaitlist] = useState(false)
	const [showWelcomeDialog, setShowWelcomeDialog] = useState(false)

	const navigation = useNavigation()
	const userId = useSelector((state) => state.user.id)

	// Ref to track if welcome dialog visibility has been closed. This is to prevent the welcome dialog from being shown multiple times
    const hasWelcomeDialogBeenClosed = useRef(false)

	useEffect(() => {
		moment.locale([txt('global.locale')])
	}, [])

	useEffect(() => {
		async function getSessionDates() {
			const sessionDatesTasks = availableDates.map(async (availableDate) => {
				const startDate = moment(availableDate.date).valueOf()
				const endDate = moment(availableDate.date).add(1, 'days').valueOf()
				const dates = await getAvailableSessions(startDate, endDate)
				return { ...availableDate, dates }
			})

			const sessionDates = await Promise.all(sessionDatesTasks)
			setSessionDates(sessionDates)
			flattenSessionDates(sessionDates);
		}

		getSessionDates()
	}, [availableDates])

	useEffect(() => {
		waitlistButtonVisibility()
	}, [sessions])

	useEffect(() => {
        // Only check welcome dialog visibility once
        if (!hasWelcomeDialogBeenClosed.current) {
            hasWelcomeDialogBeenClosed.current = true
            welcomeDialogVisibility()
        }
    }, [])

	function flattenSessionDates(sessionDates) {
		const allSessions = sessionDates.reduce((acc, session) => {
			return acc.concat(session.dates);
		}, []);
		setSessions(allSessions);
	}

	async function handleBookSession(sessionId) {
		const { bookingErrors } = txt('videoChat')

		const ERR_ENUMS = {
			LIMIT_REACHED: bookingErrors.limitReached,
			MAXIMUM_LIMIT_REACHED: bookingErrors.maximumLimitReached,
			SESSION_NOT_FOUND: bookingErrors.SessionNotFound,
			SESSION_ALREADY_BOOKED: bookingErrors.alreadyBooked,
			OWN_BOOKING: bookingErrors.ownBooking,
			DOUBLE_BOOKING: bookingErrors.doubleBook,
			DATE_EXPIRED: bookingErrors.dateExpired,
			TIMEFRAME_OCCUPIED: bookingErrors.timeframeOccupied,
			VOLUNTEER_IS_ALREADY_IN_TIMEFRAME: bookingErrors.volunteerInTimeframe,
			VOLUNTEER_ALREADY_BOOKED: bookingErrors.volunteerAlreadyBooked,
		}

		const { err_code } = await bookSession(sessionId)

		if (err_code !== undefined) {
			const errorMessage = ERR_ENUMS[err_code] ?? bookingErrors.unknown
			return setErrorMessage(errorMessage)
		}

		await handleRefresh()
	}

	async function handlePressVolunteer(userId, username) {
		if (userId) {
			const profileRes = await getVolunteerProfile(userId)
			setVolunteerProfile({ description: profileRes?.description, username })
			setVolunteerProfileVisible(true)
		}
	}

	async function handleAddToWaitlist() {
		const added = await addToWaitlist()
		if (!added) return setErrorMessage(txt('videoChat.user.getStartedDialog.waitlistError'))
		
		setShowWaitlistDialog(false)
		setShowWaitlistButton(false)
		setUserIsOnWaitlist(true)
	}
	
	async function waitlistButtonVisibility() {
		const hasWaitlistAccess = await waitlistAccess()
		if (!hasWaitlistAccess) return setShowWaitlistButton(false)

		const alreadyOnWaitlist = await getWaitlistUser(userId)
		if (alreadyOnWaitlist) {
			setShowWaitlistButton(false)
			setUserIsOnWaitlist(true)
			return 
		}

		if (sessions.length > 3) return setShowWaitlistButton(false)
		
		setShowWaitlistButton(true)
	}

	async function welcomeDialogVisibility() {
		// Only show the welcome dialog if the user has not booked any sessions before
		const prevoiusBookedSessionCount = await getPreviousBookedSessionsCount()
		if (prevoiusBookedSessionCount === 0) {
			setShowWelcomeDialog(true)
		}
	}

	function navigateToSprogland() {
		navigation.navigate('LearnDanish')
		setShowWelcomeDialog(false)
	}

	return (
		<View style={styles.container}>
			<View>
				<CustomText font="gotham-xlarge" accessibilityRole="header" aria-level={2}>
					{txt('videoChat.availableSessions')}
				</CustomText>
				{sessionDates.length === 0 && (
					<CustomText font="gotham-small" style={[styles.text, sessionDates.length === 0 && styles.noSession]}>
						{txt('videoChat.noAvailableSessions')}
					</CustomText>
				)}
			</View>

			{sessions.map((session, index) => {
				const formattedDate = formatDate(session.start_date, session.end_date);
				return (
					<View key={index} style={styles.card}>
						<View style={{ flex: 5 }}>
							<CustomText font="gotham-bold-small">{formattedDate}</CustomText>

							<CustomPressable onPress={() => handlePressVolunteer(session.volunteer_id, session.username)}>
								<CustomText font="gotham-small" style={{textDecorationLine: 'underline'}}>
									<CustomText font="gotham-small">{txt('videoChat.volunteer.title') + ': '}</CustomText>
									{session.username}
								</CustomText>
							</CustomPressable>
						</View>

						<ButtonPrimary
							title={txt('videoChat.book')}
							onPress={() => handleBookSession(session.id)}
							style={styles.button}
							titleStyle={styles.buttonTitle}
						/>
					</View>
				);
			})}

			{showWaitlistButton && (
				<ButtonPrimary
					title={txt('videoChat.user.waitlist.button')}
					onPress={() => setShowWaitlistDialog(true)}
					style={styles.waitlistButton}
					titleStyle={styles.waitlistButtonTitle}
				/>
			)}

			{userIsOnWaitlist && (
				<CustomText font="gotham-small" style={{ color: Colors.inactive, marginTop: 24 }}>
					{txt('videoChat.user.waitlist.alreadyOnWaitlist')}
				</CustomText>
			)}

			<CustomDialog visible={showWelcomeDialog} onDismiss={() => setShowWelcomeDialog(false)}>
				<CustomPressable onPress={() => setShowWelcomeDialog(false)} style={styles.iconContainer}>
					<NormalIcon name="times" size={20} color={Colors.text} />
				</CustomPressable>

				<ContentContainer>
					<CustomText font="gotham-bold-large" style={{ marginRight: 24, marginBottom: 6 }}>
						{txt('videoChat.user.welcome.title')}
					</CustomText>
					<CustomText font="gotham-small">
						{txt('videoChat.user.welcome.body')}
					</CustomText>

					
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">{txt('videoChat.user.welcome.rule1')}</CustomText>
					</View>
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">{txt('videoChat.user.welcome.rule2')}</CustomText>
					</View>
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">{txt('videoChat.user.welcome.rule3')}</CustomText>
					</View>
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">{txt('videoChat.user.welcome.rule4')}</CustomText>
					</View>
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">
							{txt('videoChat.user.welcome.rule5')}
							<CustomPressable style={{ color: Colors.blueAzure, textDecorationLine: 'underline' }} onPress={navigateToSprogland}>
								{'Sprogland'}
							</CustomPressable>
						</CustomText>
					</View>
					<View style={styles.bulletPointContainer}>
						<View style={styles.bulletPoint} />
						<CustomText font="gotham-small">{txt('videoChat.user.welcome.rule6')}</CustomText>
					</View>

					<ButtonPrimary
						title={txt('videoChat.user.welcome.button')}
						onPress={() => setShowWelcomeDialog(false)}
						style={styles.waitlistButton}
						titleStyle={styles.waitlistButtonTitle}
					/>
				</ContentContainer>
			</CustomDialog>

			<CustomDialog visible={showWaitlistDialog} onDismiss={() => setShowWaitlistDialog(false)}>
				<CustomPressable onPress={() => setShowWaitlistDialog(false)} style={styles.iconContainer}>
					<NormalIcon name="times" size={20} color={Colors.text} />
				</CustomPressable>

				<ContentContainer>
					<CustomText font="gotham-bold-large" style={{ marginRight: 24, marginBottom: 6 }}>
						{txt('videoChat.user.waitlist.title')}
					</CustomText>
					<CustomText font="gotham-small">
						{txt('videoChat.user.waitlist.body1')}
					</CustomText>
					<CustomText font="gotham-small" style={{ marginTop: 12 }}>
						{txt('videoChat.user.waitlist.body2')}
					</CustomText>

					<ButtonPrimary
						title={txt('videoChat.user.waitlist.button')}
						onPress={handleAddToWaitlist}
						style={styles.waitlistButton}
						titleStyle={styles.waitlistButtonTitle}
					/>
				</ContentContainer>
			</CustomDialog>

			<CustomDialog visible={volunteerProfileVisible} onDismiss={() => setVolunteerProfileVisible(false)}>
				<CustomPressable onPress={() => setVolunteerProfileVisible(false)} style={styles.iconContainer}>
					<NormalIcon name="times" size={20} color={Colors.text} />
				</CustomPressable>

				<ContentContainer>
					<View style={{ marginTop: 18 }}>
						<CustomText font="gotham-bold">{volunteerProfile?.username}</CustomText>
						{volunteerProfile?.description ? (
							<CustomText font="gotham" style={{ marginTop: 18 }}>{volunteerProfile?.description}</CustomText>
						) : (
							<CustomText font="gotham" style={{ marginTop: 18 }}>{txt('videoChat.user.noVolunteerProfile')}</CustomText>
						)}
					</View>
				</ContentContainer>
			</CustomDialog>
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		margin: 10,
	},
	card: {
		backgroundColor: Colors.white,
		borderRadius: 10,
		flexDirection: Layout.small ? 'column' : 'row',
		alignItems: Layout.small ? 'flex-start' : 'center',
		marginTop: 10,
		paddingVertical: Layout.small ? 18 : 12,
		paddingHorizontal: Layout.small ? 24 : 18,
	},
	text: {
		color: Colors.inactive,
		marginTop: 10,
	},
	noSession: {
		marginBottom: 60,
	},
	button: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.background,
		paddingVertical: 13,
		paddingHorizontal: 32,
		marginLeft: 12,
		minWidth: 115,
		marginTop: Layout.small ? 12 : 0,
	},
	buttonTitle: {
		color: Colors.redCross.text,
		fontSize: 14,
		fontFamily: 'gotham-bold'
	},
	iconContainer: {
		backgroundColor: Colors.greyLightLight,
		borderRadius: 12,
		padding: 8,
		position: 'absolute',
		top: 12,
		right: 12,
		zIndex: 10
	},

	waitlistButton: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.base,
		paddingVertical: 13,
		paddingHorizontal: 32,
		minWidth: 115,
		marginTop: 24,
	},
	waitlistButtonTitle: {
		color: Colors.white,
		fontSize: 14,
		fontFamily: 'gotham-bold'
	},

	bulletPoint: {
		height: 6,
		width: 6,
		borderRadius: 3,
		backgroundColor: Colors.text,
		marginRight: 12,
	},
	bulletPointContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: 8,
	},
})
